import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Calendar, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ProjectItemDialog from './ProjectItemDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status workflow — 5 stages (Azerbaijani labels confirmed by user)
const COLUMNS = [
  { key: 'todo', label: 'Yeni', color: 'bg-zinc-50 border-zinc-300', headerColor: 'text-zinc-700', overColor: 'bg-zinc-100' },
  { key: 'in_progress', label: 'İcra olunur', color: 'bg-blue-50 border-blue-300', headerColor: 'text-blue-700', overColor: 'bg-blue-100' },
  { key: 'review', label: 'Təhvilə hazırdır', color: 'bg-amber-50 border-amber-300', headerColor: 'text-amber-700', overColor: 'bg-amber-100' },
  { key: 'blocked', label: 'Problemli', color: 'bg-red-50 border-red-300', headerColor: 'text-red-700', overColor: 'bg-red-100' },
  { key: 'done', label: 'Təhvil alındı', color: 'bg-emerald-50 border-emerald-300', headerColor: 'text-emerald-700', overColor: 'bg-emerald-100' },
];

const PRIORITY = {
  low: { label: 'Aşağı', dot: 'bg-zinc-400' },
  medium: { label: 'Orta', dot: 'bg-amber-400' },
  high: { label: 'Yüksək', dot: 'bg-red-500' },
};

function isOverdue(due_date, status) {
  if (!due_date || status === 'done') return false;
  return new Date(due_date) < new Date();
}

function formatShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('az-AZ', { day: 'numeric', month: 'short' });
}

// ==================== Card ====================
function ItemCard({ item, index, members, onClick }) {
  const assignees = (item.assignee_ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter(Boolean);
  const overdue = isOverdue(item.due_date, item.status);

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => {
        const onClickHandler = () => {
          if (snapshot.isDragging) return;
          onClick?.(item);
        };
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={onClickHandler}
            style={{ ...provided.draggableProps.style }}
            className={`bg-white border rounded-md p-3 mb-2 transition-shadow cursor-grab active:cursor-grabbing ${
              snapshot.isDragging
                ? 'shadow-2xl ring-2 ring-purple-400 scale-[1.02] rotate-1'
                : 'shadow-sm hover:shadow-md'
            }`}
            data-testid={`kanban-item-${item.id}`}
          >
            <div className="flex items-start gap-2">
              <div
                className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${PRIORITY[item.priority]?.dot || 'bg-zinc-300'}`}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium leading-snug">{item.title}</div>
                {item.description && (
                  <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{item.description}</div>
                )}
              </div>
            </div>

            {(item.tags || []).length > 0 && (
              <div className="flex gap-1 flex-wrap mt-2">
                {item.tags.slice(0, 3).map((t) => (
                  <Badge key={t} variant="secondary" className="text-[10px] py-0 px-1.5">
                    {t}
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-2 gap-2">
              <div className="flex -space-x-1.5">
                {assignees.slice(0, 3).map((a) => (
                  <div
                    key={a.id}
                    title={a.full_name || a.username}
                    className="h-6 w-6 rounded-full bg-purple-100 border-2 border-white text-[10px] font-semibold flex items-center justify-center text-purple-700"
                  >
                    {(a.full_name || a.username).slice(0, 2).toUpperCase()}
                  </div>
                ))}
                {assignees.length > 3 && (
                  <div className="h-6 w-6 rounded-full bg-zinc-100 border-2 border-white text-[10px] font-semibold flex items-center justify-center text-zinc-600">
                    +{assignees.length - 3}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                {item.due_date && (
                  <div
                    className={`flex items-center gap-1 text-[11px] ${overdue ? 'text-red-600 font-semibold' : 'text-zinc-500'}`}
                    data-testid={`item-due-${item.id}`}
                  >
                    {overdue ? <AlertCircle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
                    {formatShort(item.due_date)}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </Draggable>
  );
}

// ==================== Column ====================
function Column({ col, items, members, canEdit, onAdd, onCardClick }) {
  return (
    <div
      className={`flex flex-col rounded-lg border-2 ${col.color} min-h-[420px]`}
      data-testid={`kanban-column-${col.key}`}
    >
      <div className="px-3 py-2.5 border-b flex items-center justify-between sticky top-0 bg-inherit">
        <div className={`text-sm font-semibold ${col.headerColor}`}>
          {col.label}
          <span className="ml-2 text-xs text-zinc-500 font-normal">{items.length}</span>
        </div>
        {canEdit && (
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6"
            onClick={() => onAdd(col.key)}
            title="Yeni mərhələ"
            data-testid={`add-item-${col.key}`}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Droppable droppableId={col.key}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`flex-1 p-2 overflow-y-auto transition-colors duration-150 ${
              snapshot.isDraggingOver ? `${col.overColor} ring-2 ring-inset ring-purple-300` : ''
            }`}
            data-testid={`kanban-column-body-${col.key}`}
          >
            {items.length === 0 && !snapshot.isDraggingOver && (
              <div className="text-xs text-zinc-400 italic text-center py-6">Boşdur</div>
            )}
            {items.map((it, idx) => (
              <ItemCard key={it.id} item={it} index={idx} members={members} onClick={onCardClick} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ==================== Main ====================
export default function ProjectKanbanTab({ project, user, members, canEdit, onItemsChanged }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/items`, { headers });
      setItems(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mərhələlər yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, [project?.id, headers]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const itemsByStatus = useMemo(() => {
    return COLUMNS.reduce((acc, c) => {
      acc[c.key] = items
        .filter((i) => i.status === c.key)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
      return acc;
    }, {});
  }, [items]);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const targetStatus = destination.droppableId;
    const sourceCol = [...(itemsByStatus[source.droppableId] || [])];
    const destCol =
      source.droppableId === destination.droppableId
        ? sourceCol
        : [...(itemsByStatus[destination.droppableId] || [])];

    let moved;
    if (source.droppableId === destination.droppableId) {
      [moved] = sourceCol.splice(source.index, 1);
      sourceCol.splice(destination.index, 0, { ...moved, status: targetStatus });
    } else {
      [moved] = sourceCol.splice(source.index, 1);
      destCol.splice(destination.index, 0, { ...moved, status: targetStatus });
    }

    setItems((prev) => {
      const others = prev.filter(
        (t) => t.status !== source.droppableId && t.status !== destination.droppableId
      );
      const updatedSource = sourceCol.map((t, idx) => ({ ...t, position: idx }));
      const updatedDest =
        source.droppableId === destination.droppableId
          ? []
          : destCol.map((t, idx) => ({ ...t, status: targetStatus, position: idx }));
      return [...others, ...updatedSource, ...updatedDest];
    });

    try {
      await axios.post(
        `${API}/projects/${project.id}/items/${draggableId}/move`,
        { status: targetStatus, position: destination.index },
        { headers }
      );
      onItemsChanged?.();
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Köçürmə uğursuz oldu');
      fetchItems();
    }
  };

  const handleAdd = (statusKey) => {
    setEditingItem({ status: statusKey, priority: 'medium' });
    setDialogOpen(true);
  };

  const handleCardClick = (item) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const handleSave = async (form) => {
    const payload = {
      ...form,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      parent_id: form.parent_id || null,
    };
    try {
      if (editingItem?.id) {
        await axios.put(`${API}/projects/${project.id}/items/${editingItem.id}`, payload, {
          headers,
        });
        toast.success('Yeniləndi');
      } else {
        await axios.post(`${API}/projects/${project.id}/items`, payload, { headers });
        toast.success('Mərhələ əlavə edildi');
      }
      setDialogOpen(false);
      setEditingItem(null);
      fetchItems();
      onItemsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.title}" silinsin?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/items/${item.id}`, { headers });
      toast.success('Silindi');
      setDialogOpen(false);
      setEditingItem(null);
      fetchItems();
      onItemsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  return (
    <div data-testid="project-kanban-tab">
      {loading && <div className="text-sm text-zinc-500 py-2">Yüklənir...</div>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {COLUMNS.map((col) => (
            <Column
              key={col.key}
              col={col}
              items={itemsByStatus[col.key] || []}
              members={members}
              canEdit={canEdit}
              onAdd={handleAdd}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      </DragDropContext>

      <ProjectItemDialog
        open={dialogOpen}
        item={editingItem}
        members={members}
        allItems={items}
        canEdit={canEdit}
        onClose={() => {
          setDialogOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}
